import { useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

const TEAL = "#0D5C63";
const CARD_BG = "#EDEEF0";
const WHITE = "#FFFFFF";
const BORDER = "#D8DADF";
const DARK = "#1A1A2E";
const GRAY = "#6B7280";
const GRAY_MID = "#9CA3AF";
const RED = "#C0392B";
const AMBER = "#B45309";
const GREEN = "#166534";

const fmt = (n) =>
  "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Static data for standalone history route
const INVENTORY_DATA = [
  { brand: "Coastal Craft Cocktails", product: "Coastal Craft Cocktails Nordic Gin Tonic 355 Ml", total: 551.83 },
  { brand: "Verde Cachaca", product: "Verde Cachaca Prata 750 Ml", total: 406 }
];

const INV_HISTORY = [
  { date: "Mar 19, 2026", product: "Verde Cachaca Prata 750 Ml", brand: "Verde Cachaca", type: "Outbound", qty: -42, ref: "PO 228943", warehouse: "Eastport Warehouses NJ", balance: 406, notes: "Atlas NY shipment" },
  { date: "Mar 18, 2026", product: "Tierra Tequila Blanco 750 Ml", brand: "Tierra Tequila", type: "Inbound", qty: 64, ref: "ASN 7749", warehouse: "Eastport Warehouses NJ", balance: 208.83, notes: "Container receive" },

];

export default function InventoryHistoryPage() {
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [productFilter, setProductFilter] = useState("All");
  const [tab, setTab] = useState("ledger");
  const [storageRate, setStorageRate] = useState(1.25);
  const [storageMonth, setStorageMonth] = useState("Mar");

  const brands = ["All", ...Array.from(new Set(INV_HISTORY.map((r) => r.brand)))];
  const products = ["All", ...Array.from(new Set(INV_HISTORY.map((r) => r.product)))];

  const filtered = INV_HISTORY
    .filter((r) => brandFilter === "All" || r.brand === brandFilter)
    .filter((r) => typeFilter === "All" || r.type === typeFilter)
    .filter((r) => productFilter === "All" || r.product === productFilter)
    .filter((r) => !search || r.product.toLowerCase().includes(search.toLowerCase()) || r.ref.toLowerCase().includes(search.toLowerCase()) || r.notes.toLowerCase().includes(search.toLowerCase()));

  const totalIn = filtered.filter((r) => r.qty > 0 && r.type === "Inbound").reduce((s, r) => s + r.qty, 0);
  const totalOut = filtered.filter((r) => r.type === "Outbound").reduce((s, r) => s + Math.abs(r.qty), 0);
  const totalAdj = filtered.filter((r) => r.type === "Adjustment").reduce((s, r) => s + r.qty, 0);
  const totalAlloc = filtered.filter((r) => r.type === "Allocated").reduce((s, r) => s + Math.abs(r.qty), 0);

  const brandMovement = brands.filter((b) => b !== "All").map((b) => {
    const rows = INV_HISTORY.filter((r) => r.brand === b);
    return {
      brand: b.split(" ")[0],
      inbound: rows.filter((r) => r.type === "Inbound").reduce((s, r) => s + r.qty, 0),
      outbound: rows.filter((r) => r.type === "Outbound").reduce((s, r) => s + Math.abs(r.qty), 0),
      adjustment: rows.filter((r) => r.type === "Adjustment").reduce((s, r) => s + r.qty, 0)
    };
  });

  const typeColor = (t) => {
    const cfg = {
      Inbound: { bg: "rgba(22,101,52,0.08)", text: GREEN, border: "rgba(22,101,52,0.2)", icon: "↓" },
      Outbound: { bg: "rgba(192,57,43,0.08)", text: RED, border: "rgba(192,57,43,0.2)", icon: "↑" },
      Adjustment: { bg: "rgba(180,83,9,0.08)", text: AMBER, border: "rgba(180,83,9,0.2)", icon: "±" },
      Allocated: { bg: "rgba(37,99,235,0.08)", text: "#2563EB", border: "rgba(37,99,235,0.2)", icon: "◉" }
    };
    return cfg[t] || { bg: CARD_BG, text: GRAY, border: BORDER, icon: "?" };
  };

  const computeStorage = () => {
    const allProducts = Array.from(new Set(INVENTORY_DATA.map((r) => r.product)));
    const results = [];
    allProducts.forEach((prod) => {
      const invRow = INVENTORY_DATA.find((r) => r.product === prod);
      if (!invRow) return;
      const currentBalance = invRow.total;
      const brand = invRow.brand;
      const monthHistory = INV_HISTORY.filter((r) => r.product.toLowerCase().includes(prod.toLowerCase().slice(0, 20)) && r.date.includes(storageMonth));
      const monthInbound = monthHistory.filter((r) => r.type === "Inbound").reduce((s, r) => s + r.qty, 0);
      const monthOutbound = monthHistory.filter((r) => r.type === "Outbound").reduce((s, r) => s + Math.abs(r.qty), 0);
      const beginningInv = Math.max(0, currentBalance + monthOutbound - monthInbound);
      const billableCases = beginningInv + monthInbound;
      const storageFee = billableCases * storageRate;
      if (billableCases > 0 || currentBalance > 0) {
        results.push({
          product: prod,
          brand,
          beginningInv: Math.round(beginningInv * 100) / 100,
          inbound: monthInbound,
          billableCases: Math.round(billableCases * 100) / 100,
          outbound: monthOutbound,
          endingBalance: currentBalance,
          storageFee: Math.round(storageFee * 100) / 100
        });
      }
    });
    return results.sort((a, b) => b.storageFee - a.storageFee);
  };

  const storageData = computeStorage();
  const storageBrandData = brands
    .filter((b) => b !== "All")
    .map((b) => {
      const rows = storageData.filter((r) => r.brand === b);
      return {
        brand: b.split(" ")[0],
        beginning: rows.reduce((s, r) => s + r.beginningInv, 0),
        inbound: rows.reduce((s, r) => s + r.inbound, 0),
        billable: rows.reduce((s, r) => s + r.billableCases, 0),
        fee: rows.reduce((s, r) => s + r.storageFee, 0)
      };
    })
    .filter((b) => b.billable > 0)
    .sort((a, b) => b.fee - a.fee);

  const filteredStorage = brandFilter === "All" ? storageData : storageData.filter((r) => r.brand === brandFilter);
  const filteredTotalFee = filteredStorage.reduce((s, r) => s + r.storageFee, 0);
  const filteredBillable = filteredStorage.reduce((s, r) => s + r.billableCases, 0);

  return (
    <div style={{ padding: "16px 18px", background: "#F4F5F7", minHeight: "100vh" }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: DARK, marginBottom: 12 }}>Inventory History</div>

      <div style={{ display: "flex", gap: 2, marginBottom: 16, background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", padding: 3 }}>
        {[{ id: "ledger", label: "📋 Movement Ledger" }, { id: "storage", label: "💰 Storage Fee Calculator" }].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, padding: "10px 8px", border: "none", borderRadius: 8, background: tab === t.id ? TEAL : "transparent", color: tab === t.id ? WHITE : GRAY, fontSize: 12, fontWeight: tab === t.id ? 700 : 400, cursor: "pointer" }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "storage" && (
        <div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, color: GRAY, fontWeight: 600 }}>Month:</span>
              {["Jan", "Feb", "Mar"].map((m) => (
                <button key={m} onClick={() => setStorageMonth(m)} style={{ padding: "5px 12px", border: `1px solid ${storageMonth === m ? TEAL : BORDER}`, borderRadius: 5, background: storageMonth === m ? TEAL : WHITE, color: storageMonth === m ? WHITE : DARK, fontSize: 11, cursor: "pointer" }}>
                  {m} 2026
                </button>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, color: GRAY, fontWeight: 600 }}>Rate:</span>
              <input value={storageRate} onChange={(e) => setStorageRate(parseFloat(e.target.value) || 0)} type="number" step="0.25" min="0" style={{ width: 70, padding: "5px 8px", border: `1px solid ${BORDER}`, borderRadius: 5, fontSize: 12, textAlign: "right" }} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 16 }}>
            <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 9.5, color: GRAY, fontWeight: 600 }}>TOTAL STORAGE FEE</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: TEAL }}>{fmt(filteredTotalFee)}</div>
            </div>
            <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 9.5, color: GRAY, fontWeight: 600 }}>BILLABLE CASES</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: DARK }}>{filteredBillable.toFixed(2)}</div>
            </div>
            <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 9.5, color: GRAY, fontWeight: 600 }}>SKUs</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: DARK }}>{filteredStorage.length}</div>
            </div>
          </div>

          <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 20px", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: DARK, marginBottom: 12 }}>Storage Fee by Brand</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={storageBrandData}>
                <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
                <XAxis dataKey="brand" tick={{ fontSize: 10, fill: GRAY }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: GRAY }} axisLine={false} tickLine={false} width={36} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Bar dataKey="beginning" stackId="a" fill="#CBD5E1" />
                <Bar dataKey="inbound" stackId="a" fill={GREEN} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {tab === "ledger" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10, marginBottom: 16 }}>
            {[
              { l: "Total Movements", v: String(filtered.length), color: TEAL },
              { l: "Inbound", v: `+${totalIn}`, color: GREEN },
              { l: "Outbound", v: `-${totalOut}`, color: RED },
              { l: "Adjustments", v: totalAdj >= 0 ? `+${totalAdj}` : String(totalAdj), color: AMBER },
              { l: "Allocated", v: `-${totalAlloc}`, color: "#2563EB" }
            ].map((k, i) => (
              <div key={i} style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, color: GRAY, fontWeight: 600 }}>{k.l}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.v}</div>
              </div>
            ))}
          </div>

          <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 20px", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: DARK, marginBottom: 12 }}>Net Movement by Brand</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={brandMovement}>
                <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
                <XAxis dataKey="brand" tick={{ fontSize: 10, fill: GRAY }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: GRAY }} axisLine={false} tickLine={false} width={36} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Bar dataKey="inbound" fill={GREEN} />
                <Bar dataKey="outbound" fill={RED} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
            <div style={{ position: "relative", flex: "0 0 260px" }}>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search movements..." style={{ width: "100%", padding: "7px 12px", border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 12, color: DARK, background: WHITE }} />
            </div>
            <div style={{ display: "flex", gap: 3 }}>
              {brands.map((b) => (
                <button key={b} onClick={() => setBrandFilter(b)} style={{ padding: "4px 9px", border: `1px solid ${brandFilter === b ? TEAL : BORDER}`, borderRadius: 5, background: brandFilter === b ? TEAL : WHITE, color: brandFilter === b ? WHITE : DARK, fontSize: 10, cursor: "pointer" }}>
                  {b === "All" ? "All" : b.split(" ")[0]}
                </button>
              ))}
            </div>
            <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)} style={{ border: `1px solid ${BORDER}`, borderRadius: 6, padding: "5px 8px", fontSize: 11, color: DARK, background: WHITE, cursor: "pointer" }}>
              <option value="All">All Products</option>
              {products.filter((p) => p !== "All").map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  {["Date", "Type", "Product", "Brand", "Qty", "Balance", "Reference", "Warehouse", "Notes"].map((h) => (
                    <th key={h} style={{ background: TEAL, color: WHITE, padding: "8px 10px", fontSize: 10.5, fontWeight: 600, textAlign: ["Qty", "Balance"].includes(h) ? "right" : "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const tc = typeColor(r.type);
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? WHITE : CARD_BG, borderBottom: `1px solid ${BORDER}` }}>
                      <td style={{ padding: "7px 10px", color: GRAY, whiteSpace: "nowrap", fontSize: 11 }}>{r.date}</td>
                      <td style={{ padding: "7px 10px" }}><span style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}`, borderRadius: 4, padding: "2px 7px", fontSize: 10, fontWeight: 700 }}>{tc.icon} {r.type}</span></td>
                      <td style={{ padding: "7px 10px", color: DARK, fontSize: 11 }}>{r.product}</td>
                      <td style={{ padding: "7px 10px", color: DARK, fontWeight: 500, fontSize: 11 }}>{r.brand.split(" ")[0]}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 800, color: r.qty > 0 ? GREEN : r.type === "Allocated" ? "#2563EB" : RED }}>{r.qty > 0 ? "+" : ""}{r.qty}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600, color: DARK }}>{r.balance}</td>
                      <td style={{ padding: "7px 10px", color: GRAY, fontSize: 10 }}>{r.ref}</td>
                      <td style={{ padding: "7px 10px", fontSize: 10, color: GRAY }}>{r.warehouse}</td>
                      <td style={{ padding: "7px 10px", color: GRAY_MID, fontSize: 10 }}>{r.notes || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
